import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/http";

type PackStatusResponse = {
  packId: number;
  status: "locked" | "unlocked";
  version: number | null;
};

type PackSnapshotResponse = {
  packId: number;
  status: "locked" | "unlocked";
  version: number | null;
  snapshot: {
    stores: any[];
    templates: any[];
    tasks: any[];
    assignedMediaFormats: any[];
    storeCount: number;
    templateCount: number;
    lockedAt: string;
    lockedBy: number | null;
  };
};

export function usePackSnapshot(packId: number) {
  return useQuery<PackSnapshotResponse>({
    queryKey: ['packSnapshot', packId],
    queryFn: async () => {
      const res = await apiFetch(`/api/packs/${packId}/snapshot`);
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('No locked snapshot for pack');
        }
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      return res.json();
    },
    enabled: typeof packId === "number" && packId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

export function usePackStatus(packId: number) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<PackStatusResponse>({
    queryKey: ["packStatus", packId],
    queryFn: async () => {
      const res = await apiFetch(`/api/packs/${packId}/status`);
      if (!res.ok) throw new Error(`Failed to fetch status: ${res.status}`);
      return res.json();
    },
    enabled: typeof packId === "number" && packId > 0,
    staleTime: 30_000,
  });

  const lockMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/packs/${packId}/lock`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!res.ok) throw new Error(`Failed to lock pack: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packStatus", packId] });
    },
  });

  const unlockMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/packs/${packId}/unlock`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!res.ok) throw new Error(`Failed to unlock pack: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packStatus", packId] });
    },
  });

  return {
    status: data?.status,
    version: data?.version ?? null,
    isLoading,
    isError,
    lock: () => lockMutation.mutate(),
    unlock: () => unlockMutation.mutate(),
    isLocking: lockMutation.isPending,
    isUnlocking: unlockMutation.isPending,
  };
}


